// KeepTalkingModal.js
// "Can we please keep talking?" confirmation shown when the user taps End Call
// on the global banner. Rendered inside the shared NewModal bottom-sheet:
// dismiss (the × below the sheet) = keep talking; the red End Call button
// (with a chosen reason) actually ends the call.
import React, { useState, useEffect } from "react";
import NewModal from "../NewModal/NewModal";
import { trackEvent } from "../../lib/analytics";

const REASONS = [
  "No Help required, UI is Good",
  "Annoyed, Keep it Quiet",
  "Don't need AI, Was just checking",
  "Other reason",
];

// Short GA-friendly slugs for each reason (the long text also goes as `reason`).
const REASON_KEY = {
  "No Help required, UI is Good": "ui_good_no_help",
  "Annoyed, Keep it Quiet": "annoyed_keep_quiet",
  "Don't need AI, Was just checking": "just_checking",
  "Other reason": "other",
};

const KeepTalkingModal = ({ isOpen, onKeepTalking, onEndCall }) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onKeepTalking}
      title={null}
      height="auto"
      bodyMaxHeight="80vh"
      /* This end-call confirmation must cover EVERYTHING — the call banner
         (z-index 100003) and the checkout footer (99999) included. */
      overlayZIndex={2147483000}
    >
      <div style={{ textAlign: "center", fontFamily: "Inter, system-ui, sans-serif" }}>
        {/* Red phone-cut icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#e5484d",
            margin: "4px auto 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-hidden="true"
        >
          <img
            src="/svg/phone_cut_icon2.svg"
            alt=""
            style={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }}
          />
        </div>

        <h3 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#1a1a1a" }}>
          Can we please keep talking?
        </h3>
        <p style={{ margin: "0 0 22px", fontSize: 14, color: "#777" }}>
          Seems like you accidentally clicked on 'End Call' button
        </p>

        <p style={{ margin: "0 0 6px", fontSize: 13, color: "#999", textAlign: "left" }}>
          If you really wanted to end the call, Can you select a reason?
        </p>

        <div style={{ marginBottom: 22 }}>
          {REASONS.map((r) => {
            const active = reason === r;
            return (
              <label
                key={r}
                onClick={() => setReason(r)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "14px 2px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 15, color: "#333" }}>{r}</span>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `2px solid ${active ? "#6524eb" : "#c9c9c9"}`,
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {active && (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#6524eb",
                      }}
                    />
                  )}
                </span>
              </label>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!reason}
          onClick={() => {
            if (!reason) return;
            // GA4: user CONFIRMED ending the call from the "keep talking?" modal,
            // plus WHICH reason they picked.
            trackEvent("call_end_confirmed", {
              reason,
              reason_key: REASON_KEY[reason] || "other",
              surface: "keep_talking_modal",
            });
            onEndCall(reason);
          }}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 14,
            padding: "16px",
            background: "#e5484d",
            opacity: !reason ? 0.5 : 1,
            color: "#fff",
            fontSize: 17,
            fontWeight: 800,
            cursor: !reason ? "not-allowed" : "pointer",
            transition: "opacity 0.2s ease",
          }}
        >
          End Call
        </button>
      </div>
    </NewModal>
  );
};

export default KeepTalkingModal;
